import { useEffect, useCallback, useState } from 'react'
import { useAppStore } from '@/store/app-store'
import type { NavPage } from '@/types'
import {
  isCognitoConfigured,
  handleAuthCallback,
  getUserAlias,
  getJwtToken,
  signOut,
  signInWithFederatedIdp,
  refreshSession,
} from '@/engine/cognito-auth'
import { FeedbackModal } from '@/components/layout/FeedbackModal'
import type { SideNavigationProps } from '@cloudscape-design/components'
import AppLayout from '@cloudscape-design/components/app-layout'
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group'
import Flashbar from '@cloudscape-design/components/flashbar'
import SideNavigation from '@cloudscape-design/components/side-navigation'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import TopNavigation from '@cloudscape-design/components/top-navigation'
import type { TopNavigationProps } from '@cloudscape-design/components/top-navigation'

export const PAGE_LABELS: Record<NavPage, string> = {
  overview: 'Home',
  profiles: 'Profiles',
  test: 'Test',
  results: 'Results',
  'global-metrics': 'Global Metrics',
  lifecycle: 'Model Lifecycle',
  'anthropic-health': 'Anthropic Health',
  'openai-health': 'OpenAI Health',
  'grok-health': 'Grok Health',
  settings: 'Settings',
  'capacity-blocks': 'Capacity Blocks',
  howto: 'How It Works',
  signin: 'Sign In',
}

export const NAV_ITEMS: SideNavigationProps.Item[] = [
  { type: 'link', text: 'Home', href: '/overview' },
  { type: 'link', text: 'Profiles', href: '/profiles' },
  { type: 'link', text: 'Test', href: '/test' },
  { type: 'link', text: 'Results', href: '/results' },
  { type: 'divider' },
  { type: 'link', text: 'Global Metrics', href: '/global-metrics' },
  { type: 'link', text: 'Model Lifecycle', href: '/lifecycle' },
  { type: 'divider' },
  { type: 'link', text: 'Anthropic Health', href: '/anthropic-health' },
  { type: 'link', text: 'OpenAI Health', href: '/openai-health' },
  { type: 'link', text: 'Grok Health', href: '/grok-health' },
  { type: 'divider' },
  { type: 'link', text: 'Capacity Blocks', href: '/capacity-blocks' },
  { type: 'link', text: 'Settings', href: '/settings' },
  { type: 'divider' },
  { type: 'link', text: '💬 Feedback / Bug', href: '/feedback' },
]

/** Extract page name from a href like `/test` or `#/test` → `'test'`. */
export function pageFromHref(href: string): NavPage {
  const match = href.match(/^\/?#?\/?(.+)$/)
  const candidate = match?.[1] ?? ''
  if (candidate in PAGE_LABELS) return candidate as NavPage
  return 'overview'
}

import { OverviewPage } from '@/components/overview/OverviewPage'
import { ProfileList } from '@/components/profiles/ProfileList'
import { BenchmarkPage } from '@/components/benchmark/BenchmarkPage'
import { ResultsPage } from '@/components/results/ResultsPage'
import { SettingsPanel } from '@/components/settings/SettingsPanel'
import { ModelLifecyclePage } from '@/components/lifecycle/ModelLifecyclePage'
import { AnthropicHealthPage } from '@/components/anthropic-health/AnthropicHealthPage'
import { OpenAIHealthPage } from '@/components/openai-health/OpenAIHealthPage'
import { GrokHealthPage } from '@/components/grok-health/GrokHealthPage'
import { GlobalMetricsPage } from '@/components/metrics/GlobalMetricsPage'
import { SignInPage } from '@/components/auth/SignInPage'
import { CapacityBlocksPage } from '@/components/capacity-blocks/CapacityBlocksPage'
import { cleanupStaleState } from '@/engine/scheduler-singleton'

function SettingsPage() {
  return <SettingsPanel />
}

export function App() {
  const currentPage = useAppStore((s) => s.currentPage)
  const executionMode = useAppStore((s) => s.executionMode)
  const setExecutionMode = useAppStore((s) => s.setExecutionMode)
  const setPage = useAppStore((s) => s.setPage)
  const isRunning = useAppStore((s) => s.isRunning)
  const progress = useAppStore((s) => s.progress)
  const credentialStatus = useAppStore((s) => s.credentialStatus)
  const visualMode = useAppStore((s) => s.visualMode)
  const setVisualMode = useAppStore((s) => s.setVisualMode)
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  // Cognito auth state
  const isAuthenticated = useAppStore((s) => s.isAuthenticated)
  const userAlias = useAppStore((s) => s.userAlias)
  const cognitoLoading = useAppStore((s) => s.cognitoLoading)
  const setAuthState = useAppStore((s) => s.setAuthState)
  const setCognitoLoading = useAppStore((s) => s.setCognitoLoading)
  const clearAuthState = useAppStore((s) => s.clearAuthState)

  const isCloud = executionMode === 'cloud'
  const localVerified = credentialStatus === 'verified'

  // Sync Cognito auth state: check for OAuth callback, then load existing session
  const syncAuthState = useCallback(async () => {
    if (!isCognitoConfigured()) return
    try {
      const alias = await getUserAlias()
      const token = await getJwtToken()
      if (alias && token) {
        setAuthState(true, alias, token)
        // Restore settings from cloud after successful auth
        const { restoreFromCloud } = await import('@/engine/settings-sync')
        restoreFromCloud()
      } else {
        clearAuthState()
      }
    } catch {
      clearAuthState()
    }
  }, [setAuthState, clearAuthState])

  useEffect(() => {
    if (!isCognitoConfigured()) return
    let cancelled = false

    const initAuth = async () => {
      setCognitoLoading(true)
      try {
        // Handle OAuth redirect callback (exchanges code for tokens)
        const callbackHandled = await handleAuthCallback()
        if (cancelled) return
        await syncAuthState()

        // Auto-redirect to Midway if not authenticated and no error in URL
        if (!cancelled) {
          const params = new URLSearchParams(window.location.search)
          const hasError = params.has('error') || params.has('error_description')
          const alias = await getUserAlias()
          if (!alias && !hasError && !callbackHandled) {
            // Prevent infinite redirect loops: only auto-redirect once per session
            const redirectKey = 'bedrock-diag-auto-signin'
            if (!sessionStorage.getItem(redirectKey)) {
              sessionStorage.setItem(redirectKey, '1')
              signInWithFederatedIdp('Midway-bedrock-latency')
            }
          } else if (alias) {
            // Clear the flag on successful auth
            sessionStorage.removeItem('bedrock-diag-auto-signin')
          }
        }
      } finally {
        if (!cancelled) setCognitoLoading(false)
      }
    }

    initAuth()
    return () => { cancelled = true }
  }, [setCognitoLoading, syncAuthState])

  // Periodically refresh the token (every 10 minutes) to handle expiry silently
  useEffect(() => {
    if (!isCognitoConfigured() || !isAuthenticated) return

    const interval = setInterval(async () => {
      const refreshed = await refreshSession()
      if (refreshed) {
        await syncAuthState()
      } else {
        // Silent refresh failed - clear auth so user sees Sign In again
        clearAuthState()
      }
    }, 10 * 60 * 1000)

    return () => clearInterval(interval)
  }, [isAuthenticated, syncAuthState, clearAuthState])

  const completedCalls = progress
    ? (progress.currentProfileIndex - 1) * progress.totalIterations + progress.currentIteration
    : 0
  const totalCalls = progress
    ? progress.totalProfiles * progress.totalIterations
    : 0

  const opposite = visualMode === 'dark' ? 'light' : 'dark'
  const toggleMode = () => setVisualMode(opposite)

  // Sync store with browser back/forward navigation
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname.replace(/^\//, '')
      const page = (path && path in PAGE_LABELS) ? path as NavPage : 'overview'
      // Update store without pushing another history entry
      useAppStore.setState({ currentPage: page })
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Clean up stale scheduler state on app init
  useEffect(() => {
    cleanupStaleState()
  }, [])

  // Load presets on app init (not just when visiting Profiles page)
  const presetsLoaded = useAppStore((s) => s.presetsLoaded)
  const setPresets = useAppStore((s) => s.setPresets)
  useEffect(() => {
    if (presetsLoaded) return
    import('@/engine/api-client').then(({ fetchPresets }) => fetchPresets()).then((result) => {
      // Restore saved state from localStorage
      try {
        const raw = localStorage.getItem('bedrock-diag-preset-state')
        if (raw) {
          const saved = JSON.parse(raw) as Record<string, { enabled: boolean; prompt?: string; maxTokens?: number }>
          const merged = result.map(p => {
            const s = saved[p.id]
            if (s) return { ...p, enabled: s.enabled, ...(s.prompt ? { prompt: s.prompt } : {}), ...(s.maxTokens ? { maxTokens: s.maxTokens } : {}) }
            return p
          })
          setPresets(merged)
          return
        }
      } catch { /* noop */ }
      setPresets(result)
    })
  }, [presetsLoaded, setPresets])

  const renderPage = () => {
    switch (currentPage) {
      case 'overview': return <OverviewPage />
      case 'profiles': return <ProfileList />
      case 'test': return <BenchmarkPage />
      case 'results': return <ResultsPage />
      case 'lifecycle': return <ModelLifecyclePage />
      case 'settings': return <SettingsPage />
      case 'anthropic-health': return <AnthropicHealthPage />
      case 'openai-health': return <OpenAIHealthPage />
      case 'grok-health': return <GrokHealthPage />
      case 'global-metrics': return <GlobalMetricsPage />
      case 'capacity-blocks': return <CapacityBlocksPage />
      case 'signin': return <SignInPage onSuccess={async () => {
        const alias = await getUserAlias()
        const token = await getJwtToken()
        if (alias && token) setAuthState(true, alias, token)
        setPage('overview')
      }} />
      default: return <OverviewPage />
    }
  }

  return (
    <div style={styles.app}>
      <div style={styles.main}>
        {isCognitoConfigured() && !isAuthenticated && !cognitoLoading ? (
          <>
            <TopNavigation
              identity={{ title: 'Bedrock Latency Diagnostics', href: '/' }}
              utilities={[
                {
                  type: 'button',
                  iconName: visualMode === 'dark' ? 'status-positive' : 'status-info',
                  text: visualMode === 'dark' ? 'Light mode' : 'Dark mode',
                  onClick: () => toggleMode(),
                  ariaLabel: `Switch to ${opposite} mode`,
                },
              ]}
            />
            <SignInPage />
          </>
        ) : (
        <>
        <TopNavigation
          identity={{ title: 'Bedrock Latency Diagnostics', href: '/' }}
          utilities={[
            {
              type: 'button',
              iconName: visualMode === 'dark' ? 'status-positive' : 'status-info',
              text: visualMode === 'dark' ? 'Light mode' : 'Dark mode',
              onClick: () => toggleMode(),
              ariaLabel: `Switch to ${opposite} mode`,
            },
            ...(isCognitoConfigured() && isAuthenticated
              ? [{
                  type: 'menu-dropdown' as const,
                  text: userAlias ?? 'User',
                  description: `${executionMode.toUpperCase()} mode`,
                  iconName: 'user-profile' as const,
                  items: [
                    ...(executionMode === 'cloud'
                      ? [{ id: 'local', text: 'Switch to Local', disabled: isRunning }]
                      : [{ id: 'cloud', text: 'Switch to Cloud', disabled: isRunning }]),
                    { id: 'configure', text: 'Configure Credentials' },
                    { id: 'signout', text: 'Sign out' },
                  ],
                  onItemClick: (e: CustomEvent<{ id: string }>) => {
                    if (e.detail.id === 'cloud') setExecutionMode('cloud')
                    else if (e.detail.id === 'local') setExecutionMode('local')
                    else if (e.detail.id === 'configure') setPage('settings')
                    else if (e.detail.id === 'signout') { clearAuthState(); signOut() }
                  },
                } satisfies TopNavigationProps.MenuDropdownUtility]
              : [
                  {
                    type: 'menu-dropdown' as const,
                    text: executionMode.toUpperCase(),
                    items: [
                      ...(executionMode === 'cloud'
                        ? [{ id: 'local', text: 'Switch to Local', disabled: isRunning }]
                        : [{ id: 'cloud', text: 'Switch to Cloud', disabled: isRunning }]),
                      { id: 'configure', text: 'Configure Credentials' },
                    ],
                    onItemClick: ({ detail }: { detail: { id: string } }) => {
                      if (detail.id === 'cloud') setExecutionMode('cloud')
                      else if (detail.id === 'local') setExecutionMode('local')
                      else if (detail.id === 'configure') setPage('settings')
                    },
                  } satisfies TopNavigationProps.MenuDropdownUtility,
                  ...(isCognitoConfigured() && !cognitoLoading
                    ? [{
                        type: 'button' as const,
                        text: 'Sign in',
                        onClick: () => setPage('signin'),
                      } satisfies TopNavigationProps.ButtonUtility]
                    : []),
                ]),
          ]}
        />
        <AppLayout
          navigation={
            <SideNavigation
              activeHref={`/${currentPage}`}
              items={NAV_ITEMS}
              onFollow={e => {
                e.preventDefault()
                if (e.detail.href === '/feedback') { setFeedbackOpen(true); return }
                setPage(pageFromHref(e.detail.href))
              }}
            />
          }
          breadcrumbs={
            currentPage !== 'signin' ? (
              <BreadcrumbGroup items={[
                { text: 'Bedrock Latency Diagnostics', href: '/' },
                { text: PAGE_LABELS[currentPage], href: `/${currentPage}` },
              ]} />
            ) : undefined
          }
          notifications={
            <SpaceBetween size="xs">
              <Flashbar items={[{
                type: isCloud
                  ? (isAuthenticated ? 'success' : 'warning')
                  : localVerified ? 'success' : 'warning',
                content: isCloud
                  ? (isAuthenticated
                    ? 'Cloud Mode'
                    : <span>Cloud Mode - <a href="/signin" onClick={(e) => { e.preventDefault(); setPage('signin') }} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Sign In Required</a></span>)
                  : !localVerified
                    ? <span>Local Mode — requires your own <a href="/settings" onClick={(e) => { e.preventDefault(); setPage('settings') }} style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>credentials</a>, which are currently missing.</span>
                    : 'Local Mode',
                dismissible: false,
              }]} />
              {isRunning && progress && (
                <StatusIndicator type="loading">
                  Benchmark running: {completedCalls}/{totalCalls} calls complete
                </StatusIndicator>
              )}
            </SpaceBetween>
          }
          content={renderPage()}
          toolsHide={true}
          navigationWidth={220}
        />
        {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
        </>
        )}
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  app: { display: 'flex', flexDirection: 'column' as const, minHeight: '100vh' },
  main: { flex: 1, display: 'flex', flexDirection: 'column' as const, minWidth: 0 },
  content: { flex: 1, overflowY: 'auto' as const }
}
